import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';

export interface VersionInfo {
  version: string;
  buildDate: string;
  branchInfo?: string;
  mainjs?: string;
  maincss?: string;
}

@Injectable({ providedIn: 'root' })
export class ReactEmbeddedVersionService {
  private readonly version = 'version.json';
  private readonly versionCache = new Map<string, VersionInfo | null>();

  constructor(
    private http: HttpClient,
    @Inject(CbreLoggerToken) private readonly logger: CbreLogger
  ) {}

  displayVersion(baseURl: string): string | null {
    const url = `${baseURl.replace(/\/+$/, '')}/${this.version}`;
    return this.versionCache.get(url)?.version ?? null;
  }

  getVersion(baseURl: string): Observable<VersionInfo | null> {
    const url = `${baseURl.replace(/\/+$/, '')}/${this.version}`;

    // Cache-bust: version.json is fetched with no server-side cache-control
    // guarantees, and local dev proxies/http-servers routinely serve stale
    // copies even after a hard refresh. A unique query param forces a fresh
    // network fetch every time regardless of any Cache-Control headers.
    return this.http.get<VersionInfo>(url, { params: { _: Date.now() } }).pipe(
      tap(info => {
        this.versionCache.set(url, info);
      }),
      catchError(err => {
        // Fail gracefully: log and return null so callers can handle absence
        // of version info without throwing.
        // Keep logging minimal so it's safe in production environments.
        const errorMessage = err instanceof Error ? err.message : String(err ?? 'unknown error');
        this.logger.error(`Failed to fetch version.json: ${errorMessage}`);
        return of(null);
      })
    );
  }
}
